(function(){
  function isMobile() {
    return window.innerWidth < 1024;
  }
  class ScrollIndicator extends HTMLElement {
    connectedCallback() {
      this.observeContainer = document.querySelector(this.dataset.observeContainer);
      this.observerItem = document.querySelectorAll(this.dataset.observeItem);
      this.indicators = this.querySelectorAll('button');
      this.container = this.querySelector('div');

      if (!this.observeContainer || !this.observerItem || !this.indicators.length || !this.container) return;

      this.observer = null;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.indicatorActiveClass = this.dataset.indicatorActiveClass?.split(' ') ?? [];
      this.indicatorDefaultClass = this.dataset.indicatorDefaultClass?.split(' ') ?? [];
      this.canScrollUpClass = this.dataset.canScrollUpClass;
      this.canScrollDownClass = this.dataset.canScrollDownClass;
      this.visibleItems = 10;

      this.init();
    }

    _handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const currentSlideIndex = entry.target.dataset.scrollIndex;
          const visibleIndicators = [];
          let currentVisibleIndex = 0;

          this.indicators.forEach((indicator) => {
            if (indicator.dataset.index === currentSlideIndex) {
              indicator.classList.add(...this.indicatorActiveClass);
              indicator.classList.remove(...this.indicatorDefaultClass);
              indicator.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
              currentVisibleIndex = visibleIndicators.length;
            } else {
              indicator.classList.remove(...this.indicatorActiveClass);
              indicator.classList.add(...this.indicatorDefaultClass);
            }

            if(window.theme.utils.isElementVisible(indicator)) {
              visibleIndicators.push(indicator);
            }
          });

          // Scroll early to start/end if the current item is within the visible items count
          const nearEndThreshold = visibleIndicators.length - this.visibleItems;
          const hasOverflow = visibleIndicators.length > this.visibleItems;
          if (hasOverflow && currentVisibleIndex >= nearEndThreshold) {
            if(isMobile()) {
              this.container.scrollTo({ left: this.container.scrollWidth, behavior: 'smooth' });
            } else {
              this.container.scrollTo({ top: this.container.scrollHeight, behavior: 'smooth' });
            }            
          } else if (hasOverflow && currentVisibleIndex < this.visibleItems) {
            if(isMobile()) {
              this.container.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              this.container.scrollTo({ top: 0, behavior: 'smooth' });
            }            
          }

          this._updateScrollClasses();
        }
      });
    };

    _updateScrollClasses = (removeClasses = false) => {
      if (this.dataset.canScrollUpClass && removeClasses)
        this.container.classList.remove(this.dataset.canScrollUpClass);

      if (this.dataset.canScrollDownClass && removeClasses)
        this.container.classList.remove(this.dataset.canScrollDownClass);

      let canScrollUp = false, canScrollDown = false;

      if (isMobile()) {
        const { scrollLeft, scrollWidth, clientWidth } = this.container;
        canScrollUp = scrollLeft > 0;
        canScrollDown = scrollLeft + clientWidth < scrollWidth - 1;
      } else {
        const { scrollTop, scrollHeight, clientHeight } = this.container;
        canScrollUp = scrollTop > 0;
        canScrollDown = scrollTop + clientHeight < scrollHeight - 2;
      }

      if (this.dataset.canScrollUpClass) this.container.classList.toggle(this.dataset.canScrollUpClass, canScrollUp);

      if (this.dataset.canScrollDownClass) this.container.classList.toggle(this.dataset.canScrollDownClass, canScrollDown);
    };

    init() {
      this.observer = new IntersectionObserver(this._handleIntersection, {
        root: this.observeContainer,
        rootMargin: '0px',
        threshold: 0.75,
      });

      this.observerItem.forEach((item) => {
        this.observer.observe(item);
      });

      this.indicators.forEach((dot) => {
        dot.addEventListener(
          'click',
          (evt) => {
            const index = evt.currentTarget.dataset.index;
            const target = Array.from(this.observerItem).filter((item) => item.dataset.scrollIndex === index);

            if (target.length) {
              target[0].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          },
          { signal: this.signal }
        );
      });

      this.container.addEventListener('scroll', this._updateScrollClasses, { passive: true, signal: this.signal });

      document.addEventListener(
        'resetProductSlider',
        () => {
          this.observerItem.forEach((item) => {
            const currentIndex = +item.dataset.scrollIndex;
            const indicator = this.indicators[currentIndex];

            if (!indicator) return;

          if (window.theme.utils.isElementVisible(item)) {
            indicator.style.display = '';
          } else {
            indicator.style.display = 'none';
          }
        });

        if(isMobile()) {
          this.observeContainer.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.observeContainer.scrollTo({ top: 0, behavior: 'smooth' });
        }

        this._updateScrollClasses(true);
      }, { signal: this.signal });
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observerItem.forEach((item) => {
          this.observer.unobserve(item);
        });
      }
      this.abortController?.abort();
    }
  }

  if (!customElements.get('scroll-indicator')) {
    customElements.define('scroll-indicator', ScrollIndicator);
  }
  class ImageThumbnails extends ScrollIndicator {
    connectedCallback() {
      super.connectedCallback();

      this.indicators = this.querySelectorAll('button.thumbnail-image ');
      this.container = this.querySelector('div.thumbnail-container');
      this.scrollUp = this.querySelector('button.scroll-up');
      this.scrollDown = this.querySelector('button.scroll-down');
      this.visibleItems = 4;

      if (!this.container || !this.scrollUp || !this.scrollDown) return;

      this._updateScrollButtonVisibility();
      this._bindScrollEvents();
    }

    _updateScrollButtonVisibility() {
      const { scrollTop, scrollHeight, clientHeight } = this.container;

      // Show scroll-up only if we can scroll up
      this.scrollUp.classList.toggle('tw-hidden', scrollTop <= 0);

      // Show scroll-down only if we can scroll down
      this.scrollDown.classList.toggle('tw-hidden', scrollTop + clientHeight >= scrollHeight);
    }

    _bindScrollEvents() {
      this.container.addEventListener(
        'scroll',
        () => {
          this._updateScrollButtonVisibility();
        },
        { passive: true, signal: this.signal }
      );

      this.scrollUp.addEventListener(
        'click',
        () => {
          this.container.scrollBy({
            top: -this.container.clientHeight,
            behavior: 'smooth',
          });
        },
        { signal: this.signal }
      );

      this.scrollDown.addEventListener(
        'click',
        () => {
          this.container.scrollBy({
            top: this.container.clientHeight,
            behavior: 'smooth',
          });
        },
        { signal: this.signal }
      );
    }
  }

  if (!customElements.get('image-thumbnails')) {
    customElements.define('image-thumbnails', ImageThumbnails);
  }

  class InlineReadMore extends HTMLElement {
    connectedCallback() {
      this.container = this.querySelector('div');

      if (!this.container) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.isLinkVariant = this.dataset.variant === 'link';
      this.resizeTimeout = null;

      this.init();
    }

    _truncateDescription(maxChars) {
      const text = this.container.textContent.trim();
      const truncated = text.substring(0, maxChars).trim();

      // Find last space to avoid cutting words
      const lastSpace = truncated.lastIndexOf(' ');
      const finalText = lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated;

      if (this.isLinkVariant) {
        // Link variant - render a link instead of a button
        const linkText = this.dataset.linkText || 'View Details';
        const linkHref = this.dataset.linkHref || '#';
        this.container.innerHTML = `<p class="tw-m-0">${finalText}… <a href="${linkHref}" target="_blank" rel="noopener noreferrer" class="${this.dataset.buttonClass}">${linkText}</a></p>`;
      } else {
        // Default variant - render a button
        this.container.innerHTML = `<p class="tw-m-0">${finalText}… <button class="description-toggle-btn ${this.dataset.buttonClass}">Read More</button></p>`;
        this._bindDescriptionToggle();
      }
    }

    _getCharacterLimit() {
      const mobileLimit = Number.parseInt(this.dataset.mobileCharLimit, 10);
      const desktopLimit = Number.parseInt(this.dataset.charLimit, 10);

      return window.innerWidth < 1024 ? (Number.isFinite(mobileLimit) ? mobileLimit : 120) : Number.isFinite(desktopLimit) ? desktopLimit : 180;
    }

    _bindDescriptionToggle() {
      const button = this.container.querySelector('.description-toggle-btn');

      if (button) {
        button.addEventListener('click', () => this._toggleDescription(), { signal: this.signal });
      }
    }

    _toggleDescription() {
      if (!this.originalDescription) return;

      this.descriptionExpanded = !this.descriptionExpanded;

      if (this.descriptionExpanded) {
        // Expand - show full content with "Read Less" inline
        this.container.innerHTML = `${this.originalDescription} <button class="description-toggle-btn ${this.dataset.buttonClass}">Read Less</button>`;
        this._bindDescriptionToggle();
      } else {
        // Collapse - truncate again
        const maxChars = this._getCharacterLimit();
        this._truncateDescription(maxChars);
      }
    }

    init() {
      // Store original HTML content
      this.originalDescription = this.container.innerHTML;
      const initialClasses = (this.dataset.initialClass ?? '').split(' ').filter(Boolean);
      if (initialClasses.length) this.container.classList.remove(...initialClasses);

      // Get text content and check if truncation is needed
      const text = this.container.textContent.trim();
      const maxChars = this._getCharacterLimit();

      if (text.length > maxChars) {
        this._truncateDescription(maxChars);
      }

      // Recheck on window resize
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          if (!this.descriptionExpanded) {
            const newMaxChars = this._getCharacterLimit();
            this.container.innerHTML = this.originalDescription;
            const text = this.container.textContent.trim();

              if (text.length > newMaxChars) {
                this._truncateDescription(newMaxChars);
              }
            }
          }, 150);
        },
        { signal: this.signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.resizeTimeout);
    }
  }

  if (!customElements.get('inline-read-more')) {
    customElements.define('inline-read-more', InlineReadMore);
  }

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.showVariantsButton = this.querySelector('.js-show-variants-button');
      this.variantsContainer = this.querySelector('.js-variants-container');
      this.hideClass = this.dataset.hideClass;

      if (!this.showVariantsButton || !this.variantsContainer) return;

      this.variantButtons = this.variantsContainer.querySelectorAll('button');
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.init();
    }

    _reset() {
      this.showVariantsButton.classList.remove(this.hideClass);
      this.variantsContainer.classList.add(this.hideClass);
      this.variantsContainer.classList.remove('tw-cursor-wait');
      this.variantButtons.forEach((el) => (el.disabled = false));
    }

    _addItem(id) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: 'quantity=1&id=' + id,
      })
        .then((response) => {
          if (!response.ok) throw new Error('Add to cart failed');
          return response.json();
        })
        .then((item) => {
          document.dispatchEvent(new CustomEvent('cart:update', { detail: { addedItems: [item] } }));
          this._reset();
        })
        .catch(() => {
          this._reset();
        });
    }

    init() {
      this.variantButtons.forEach((el) => {
        el.addEventListener(
          'click',
          (evt) => {
            this.variantButtons.forEach((el) => (el.disabled = true));
            this.variantsContainer.classList.add('tw-cursor-wait');
            this._addItem(evt.currentTarget.value);
          },
          { signal: this.signal }
        );
      });

      this.showVariantsButton.addEventListener(
        'click',
        () => {
          this.showVariantsButton.classList.add(this.hideClass);
          this.variantsContainer.classList.remove(this.hideClass);
        },
        { signal: this.signal }
      );

      const eventType = 'ontouchstart' in window ? 'touchstart' : 'mousedown';
      const handleClickAway = (evt) => {
        if (!this.variantsContainer.contains(evt.target) && !this.showVariantsButton.contains(evt.target)) {
          this._reset();
        }
      };

      document.addEventListener(eventType, handleClickAway, { signal: this.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }
  }

  if (!customElements.get('quick-add')) {
    customElements.define('quick-add', QuickAdd);
  }

  function setAtcSlideVisible(el, visible) {
    el.classList.toggle('tw-opacity-0', !visible);
    el.classList.toggle('tw-translate-y-full', !visible);
    el.classList.toggle('tw-opacity-100', visible);
    el.classList.toggle('tw-translate-y-0', visible);
    el.toggleAttribute('inert', !visible);
    if (visible) {
      el.removeAttribute('aria-hidden');
    } else {
      el.setAttribute('aria-hidden', 'true');
    }
  }

  function updateMobileChatForAtc(host, visible) {
    let chatButton = host.chatButton || document.querySelector('iframe#chat-button');
    host.chatButton = chatButton;
    if (!chatButton || !isMobile()) return;

    chatButton.style.bottom = visible ? '60px' : '4px';
  }

  function initAtcHost(host, onUpdate) {
    host.abortController = new AbortController();
    host.signal = host.abortController.signal;
    host.scrollThreshold = 50;
    host.isVisible = false;
    host.chatButton = document.querySelector('iframe#chat-button');
    host.resizeTimeout = null;
    host._scrollRafId = null;

    let scrollTicking = false;
    const throttledScrollHandler = () => {
      if (!scrollTicking) {
        scrollTicking = true;
        host._scrollRafId = requestAnimationFrame(() => {
          onUpdate();
          scrollTicking = false;
        });
      }
    };

    const debouncedResizeHandler = () => {
      clearTimeout(host.resizeTimeout);
      host.resizeTimeout = setTimeout(onUpdate, 100);
    };

    window.addEventListener('scroll', throttledScrollHandler, { passive: true, signal: host.signal });
    window.addEventListener('resize', debouncedResizeHandler, { signal: host.signal });

    onUpdate();

    if (window.GorgiasChat && host.chatButton) {
      updateMobileChatForAtc(host, host._isChatOffsetVisible());
    }

    window.addEventListener('gorgias-widget-loaded', () => {
      host.chatButton = document.querySelector('iframe#chat-button');
      updateMobileChatForAtc(host, host._isChatOffsetVisible());
    }, { signal: host.signal });
  }

  // Panel Add to Bag: switches Docked / Pinned / Hidden from static position.
  class PdpDockedAtc extends HTMLElement {
    connectedCallback() {
      this.flowRoot = this.closest('.pdp-docked-atc-slot');
      this.placement = null;
      this._staticSide = null;
      this._lastIsMobile = null;
      this._pinAnim = null;

      initAtcHost(this, () => this._updatePlacement());
    }

    _isChatOffsetVisible() {
      return this.placement === 'pinned';
    }

    _clearPinnedStyles() {
      this.removeAttribute('style');
      this.classList.remove('tw-fixed', 'tw-bottom-3', 'tw-left-3', 'tw-right-3');
    }

    _reserveFlowSpace() {
      const height = Math.ceil(this.getBoundingClientRect().height) || this.flowRoot.offsetHeight;
      if (height > 0) {
        this.flowRoot.style.minHeight = `${height}px`;
      }
    }

    _pinToMobileViewport() {
      this.classList.add('tw-fixed', 'tw-bottom-3', 'tw-left-3', 'tw-right-3');
    }

    _pinToDesktopPanel() {
      const slotRect = this.flowRoot.getBoundingClientRect();
      this.classList.add('tw-fixed');
      this.style.position = 'fixed';
      this.style.left = `${slotRect.left}px`;
      this.style.width = `${slotRect.width}px`;
      this.style.bottom = '12px';
    }

    _settleDocked() {
      this.flowRoot.style.minHeight = '';
      this._clearPinnedStyles();
      this.style.position = 'relative';
      setAtcSlideVisible(this, true);
      updateMobileChatForAtc(this, false);
    }

    // Mobile Docked↔Pinned: element is already visible, so CSS class transitions
    // have no start state. Theme Tailwind uses !important, which beats WAAPI
    // while those utilities stay on — strip them, animate, restore on finish.
    _animateMobilePin(direction) {
      this._pinAnim?.cancel();
      this.classList.remove('tw-opacity-0', 'tw-translate-y-full', 'tw-opacity-100', 'tw-translate-y-0');
      const off = { opacity: 0, transform: 'translateY(100%)' };
      const on = { opacity: 1, transform: 'translateY(0)' };
      const anim = this.animate(
        direction === 'in' ? [off, on] : [on, off],
        { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );
      this._pinAnim = anim;
      anim.onfinish = () => {
        if (this._pinAnim !== anim) return;
        this._pinAnim = null;
        if (direction === 'in') {
          setAtcSlideVisible(this, true);
        } else {
          this._settleDocked();
        }
      };
      anim.oncancel = () => {
        if (this._pinAnim === anim) this._pinAnim = null;
      };
    }

    _applyPlacement(placement, previous) {
      switch (placement) {
        case 'docked':
          if (isMobile() && previous === 'pinned' && this._staticSide === 'above') {
            // Scroll-up dock — the bar slides out at the bottom; the control appears docked on finish.
            updateMobileChatForAtc(this, false);
            this._animateMobilePin('out');
            return;
          }
          this._pinAnim?.cancel();
          this._settleDocked();
          return;
        case 'hidden':
          this._pinAnim?.cancel();
          this._reserveFlowSpace();
          this._clearPinnedStyles();
          this._pinToMobileViewport();
          setAtcSlideVisible(this, false);
          updateMobileChatForAtc(this, false);
          return;
        case 'pinned':
          this._reserveFlowSpace();
          this._clearPinnedStyles();
          if (isMobile()) {
            this._pinAnim?.cancel();
            this._pinToMobileViewport();
            if (previous === 'docked' && this._staticSide === 'above') {
              this._animateMobilePin('in');
            } else {
              setAtcSlideVisible(this, true);
            }
            updateMobileChatForAtc(this, true);
            return;
          }
          setAtcSlideVisible(this, true);
          this._pinToDesktopPanel();
          updateMobileChatForAtc(this, false);
          return;
      }
    }

    _updatePlacement() {
      const mobile = isMobile();
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const rect = this.flowRoot.getBoundingClientRect();
      let placement;

      if (mobile && scrollTop <= this.scrollThreshold) {
        placement = 'hidden';
      } else if (rect.top >= 0 && rect.bottom <= window.innerHeight) {
        placement = 'docked';
      } else if (!mobile && rect.top < 0) {
        // Desktop one-way latch: while any part of the static slot is above the
        // viewport top (scrolled past, or partially leaving/entering through the
        // top), stay Docked. Re-Pin only after the slot is fully below that line
        // again (scroll back up so it is no longer fully in view from above).
        placement = 'docked';
      } else {
        placement = 'pinned';
      }

      if (placement !== 'docked') this._staticSide = rect.top < 0 ? 'above' : 'below';

      const modeChanged = mobile !== this._lastIsMobile;
      this._lastIsMobile = mobile;

      if (placement === this.placement && !modeChanged) {
        // Desktop pinned: refresh column bounds only while scrolling.
        if (placement === 'pinned' && !mobile) {
          this._pinToDesktopPanel();
        }
        return;
      }

      const previous = this.placement;
      this.placement = placement;
      this.isVisible = placement !== 'hidden';
      this._applyPlacement(placement, previous);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.resizeTimeout);
      cancelAnimationFrame(this._scrollRafId);
      this._pinAnim?.cancel();
      this.chatButton = null;
    }
  }

  if (!customElements.get('pdp-docked-atc')) {
    customElements.define('pdp-docked-atc', PdpDockedAtc);
  }
})();
